<template>
  <div class="survey-builder">
    <a-form-model
      layout="horizontal"
      :model="surveyForm"
      class="survey-builder__form"
      :label-col="{ span: 24 }"
      :wrapper-col="{ span: 24 }"
      ref="surveyFormRef"
    >
      <div class="survey-section" data-testid="document-form-survey-settings-section">
        <div class="survey-section__header">
          <div>
            <h4>Quiz settings</h4>
            <p>Set the passing score and add optional instructions for associates.</p>
          </div>
        </div>

        <div class="survey-settings">
          <a-form-model-item
            v-if="surveyForm.questions.length"
            :colon="false"
            label-align="left"
            label="Passing Score required (%)"
            extra="Choose how many correct answers are required to pass this quiz."
          >
            <a-slider
              v-model="surveyForm.completion_rate"
              :tipFormatter="formatRange"
              :min="0"
              :max="surveyForm.questions.length"
              :step="1"
              data-testid="document-form-survey-passing-score-slider"
            />
            <div class="survey-score-preview">{{ formatRange(surveyForm.completion_rate) }} required</div>
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Details"
            extra="Use this field for context, instructions, or rules the associate should know before answering."
          >
            <a-textarea
              allow-clear
              v-model="surveyForm.details"
              placeholder="Add instructions or context for this quiz"
              :auto-size="{ minRows: 3, maxRows: 6 }"
              data-testid="document-form-survey-details-input"
            />
          </a-form-model-item>
        </div>
      </div>

      <div class="survey-section survey-section_questions" data-testid="document-form-survey-questions-section">
        <div class="survey-section__header survey-section__header_actions">
          <div>
            <h4>Questions</h4>
            <p>Add questions, mark correct answers, and drag rows to reorder the quiz.</p>
          </div>
          <a-button
            type="primary"
            icon="plus"
            @click="addQuestion"
            data-testid="document-form-survey-add-question-button"
          >
            Add Question
          </a-button>
        </div>

        <div v-if="!surveyForm.questions.length" class="survey-empty-state" data-testid="document-form-survey-empty-state">
          <a-empty description="No questions yet">
            <p class="survey-empty-state__hint">
              Add your first question to start building the quiz. Each question needs at least 2 answer options and one
              correct answer.
            </p>
            <a-button type="primary" @click="addQuestion" data-testid="document-form-survey-add-first-question-button">
              Add first question
            </a-button>
          </a-empty>
        </div>

        <draggable
          v-else
          ref="questionsWrapper"
          class="wrapper"
          :list="surveyForm.questions"
          handle=".question-drag-handle"
          v-bind="dragOptions"
          @start="draggingQuestions = true"
          @end="draggingQuestions = false"
        >
          <transition-group
            tag="div"
            class="questions-list"
            type="transition"
            :name="!draggingQuestions ? 'flip-list' : null"
          >
            <div
              v-for="(question, qIndex) in surveyForm.questions"
              :key="question.id"
              :ref="`question-${question.id}`"
              class="question-item"
              :class="{ 'question-item_invalid': isQuestionInvalid(question) }"
              data-testid="document-form-survey-question-item"
            >
              <div class="question-header">
                <div>
                  <h4 class="m-0">Question {{ qIndex + 1 }}</h4>
                  <p>{{ question.answers.length }} answers</p>
                </div>
                <div class="question-header-actions">
                  <a-button
                    type="danger"
                    icon="close"
                    @click="confirmRemoveQuestion(qIndex)"
                    data-testid="document-form-survey-delete-question-button"
                  >
                    Delete Question
                  </a-button>
                  <span class="question-drag-handle question-drag-grip" title="Drag question">
                    <span class="drag-grip-dots" />
                  </span>
                </div>
              </div>

              <a-form-model-item
                :label="`Question №${qIndex + 1}`"
                :prop="'questions.' + qIndex + '.name'"
                :rules="[{ required: true, message: 'Please input the question!' }]"
              >
                <a-input :ref="`questionInput-${question.id}`" v-model="question.name" placeholder="Question" />
              </a-form-model-item>

              <a-form-model-item label="Description">
                <a-input v-model="question.description" placeholder="Description" />
              </a-form-model-item>

              <a-form-model-item label="Question Type">
                <a-radio-group
                  :value="question.type"
                  button-style="solid"
                  @change="changeQuestionType(qIndex, $event.target.value)"
                >
                  <a-radio-button value="single">Single</a-radio-button>
                  <a-radio-button value="multiple">Multiple</a-radio-button>
                </a-radio-group>
              </a-form-model-item>

              <div class="answers-block">
                <div class="answers-block__header">
                  <span>Answers</span>
                  <span>Drag answers to set their order.</span>
                </div>

                <draggable
                  class="answers-wrapper"
                  :list="question.answers"
                  handle=".answer-drag-handle"
                  v-bind="answerDragOptions"
                  @start="draggingAnswers = true"
                  @end="draggingAnswers = false"
                >
                  <transition-group
                    tag="div"
                    class="answers-list"
                    type="transition"
                    :name="!draggingAnswers ? 'flip-list' : null"
                  >
                    <div v-for="(answer, aIndex) in question.answers" :key="answer.id" class="answer-item">
                      <a-form-model-item
                        :label="`Answer №${aIndex + 1}`"
                        :prop="'questions.' + qIndex + '.answers.' + aIndex + '.name'"
                        :rules="[{ required: true, message: 'Please input the answer!' }]"
                      >
                        <div class="answer-input-row">
                          <a-input class="answer-input" v-model="answer.name" placeholder="Answer">
                            <a-icon
                              slot="suffix"
                              :type="answer.correct ? 'check-circle' : 'close-circle'"
                              :style="{ color: answer.correct ? '#10b981' : '#ef4444' }"
                            />
                          </a-input>
                          <a-button type="danger" icon="close" @click="removeAnswer(qIndex, aIndex)" />
                          <span class="answer-drag-handle answer-drag-grip" title="Drag answer">
                            <span class="drag-grip-dots" />
                          </span>
                        </div>
                      </a-form-model-item>

                      <div class="answer-correct-row">
                        <a-radio
                          v-if="question.type === 'single'"
                          :checked="answer.correct"
                          @change="setSingleCorrect(qIndex, aIndex)"
                        >
                          Correct
                        </a-radio>
                        <a-checkbox v-else v-model="answer.correct">Correct</a-checkbox>
                      </div>
                    </div>
                  </transition-group>
                </draggable>
              </div>

              <div v-if="isQuestionAnswersCountInvalid(question)" class="question-validation-error">
                Add at least two answers to continue
              </div>
              <div v-else-if="isQuestionCorrectAnswerInvalid(question)" class="question-validation-error">
                Add at least one correct answer to continue
              </div>

              <a-button
                type="dashed"
                @click="addAnswer(qIndex)"
                style="width: 100%; margin-top: 12px"
                data-testid="document-form-survey-add-answer-button"
              >
                Add Answer
              </a-button>
            </div>
          </transition-group>
        </draggable>
      </div>
    </a-form-model>
  </div>
</template>

<script>
import SortableList from '@/components/ui/sortable-list.vue'
import { Button, Checkbox, Empty, FormModel, Icon, Input, Modal, notification, Radio, Slider } from 'ant-design-vue'

export default {
  components: {
    draggable: SortableList,
    'a-button': Button,
    'a-checkbox': Checkbox,
    'a-empty': Empty,
    'a-input': Input,
    'a-icon': Icon,
    'a-slider': Slider,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-radio': Radio,
    'a-radio-group': Radio.Group,
    'a-radio-button': Radio.Button,
    'a-textarea': Input.TextArea
  },
  props: {
    survey: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      surveyForm: {
        questions: [],
        completion_rate: 0,
        details: undefined
      },
      draggingQuestions: false,
      draggingAnswers: false
    }
  },
  computed: {
    dragOptions() {
      return {
        animation: 200,
        ghostClass: 'ghost',
        chosenClass: 'drag-chosen',
        dragClass: 'drag-active'
      }
    },

    answerDragOptions() {
      return {
        animation: 200,
        ghostClass: 'ghost',
        chosenClass: 'drag-chosen',
        dragClass: 'drag-active'
      }
    }
  },
  watch: {
    survey: {
      handler(newVal) {
        if (newVal) {
          const questions = newVal.questions ?? []
          this.surveyForm = {
            ...newVal,
            completion_rate: (newVal.completion_rate / 100) * questions.length,
            questions: questions.map(question => this.normalizeQuestion(question))
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    formatRange(value) {
      const total = this.surveyForm.questions.length
      if (total === 0) {
        return '0/0 (0%)'
      }
      const percentage = ((value / total) * 100).toFixed(0)
      return `${value}/${total} (${percentage}%)`
    },

    generateUUID() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
      })
    },

    normalizeQuestion(question) {
      const answers = (question.answers ?? []).map(answer => ({
        ...answer,
        correct: Boolean(answer.correct)
      }))
      const type = ['single', 'multiple'].includes(question.type)
        ? question.type
        : answers.length > 1
          ? 'multiple'
          : 'single'

      return {
        ...question,
        type,
        answers: type === 'single' ? this.leaveOnlyFirstCorrectAnswer(answers) : answers
      }
    },

    createAnswer(questionId, correct = false) {
      return {
        id: this.generateUUID(),
        question_id: questionId,
        name: '',
        correct
      }
    },

    addQuestion() {
      const questionId = this.generateUUID()
      this.surveyForm.questions.push({
        id: questionId,
        type: 'single',
        name: '',
        description: '',
        answers: [this.createAnswer(questionId, true), this.createAnswer(questionId)]
      })

      this.focusQuestion(questionId)
    },

    focusQuestion(questionId) {
      this.$nextTick(() => {
        const questionRef = this.$refs[`question-${questionId}`]
        const questionComponent = Array.isArray(questionRef) ? questionRef[0] : questionRef
        const questionElement = questionComponent?.$el ?? questionComponent
        if (questionElement?.scrollIntoView) {
          questionElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }

        const inputRef = this.$refs[`questionInput-${questionId}`]
        const inputComponent = Array.isArray(inputRef) ? inputRef[0] : inputRef
        if (inputComponent?.focus) {
          inputComponent.focus()
        }
      })
    },

    confirmRemoveQuestion(index) {
      Modal.confirm({
        title: 'Are you sure?',
        content: 'Are you sure you want to delete this question?',
        okType: 'danger',
        onOk: () => this.removeQuestion(index)
      })
    },

    removeQuestion(index) {
      this.surveyForm.questions.splice(index, 1)
      this.surveyForm.completion_rate = Math.min(this.surveyForm.completion_rate, this.surveyForm.questions.length)
    },

    addAnswer(questionIndex) {
      this.surveyForm.questions[questionIndex].answers.push(
        this.createAnswer(this.surveyForm.questions[questionIndex].id)
      )
    },

    removeAnswer(questionIndex, answerIndex) {
      const question = this.surveyForm.questions[questionIndex]
      question.answers.splice(answerIndex, 1)

      if (question.type === 'single' && question.answers.length && !question.answers.some(answer => answer.correct)) {
        this.$set(question.answers[0], 'correct', true)
      }
    },

    changeQuestionType(questionIndex, type) {
      const question = this.surveyForm.questions[questionIndex]
      const correctCount = question.answers.filter(answer => answer.correct).length

      this.$set(question, 'type', type)

      if (type === 'single') {
        question.answers = this.leaveOnlyFirstCorrectAnswer(question.answers)
      }

      if (type === 'single' && correctCount > 1) {
        this.showNotification('warning', 'Warning', 'Only one correct answer is left')
      }
    },

    leaveOnlyFirstCorrectAnswer(answers) {
      let hasCorrectAnswer = false

      const normalizedAnswers = answers.map(answer => {
        if (!answer.correct || hasCorrectAnswer) {
          return {
            ...answer,
            correct: false
          }
        }

        hasCorrectAnswer = true
        return {
          ...answer,
          correct: true
        }
      })

      if (!hasCorrectAnswer && normalizedAnswers.length) {
        return normalizedAnswers.map((answer, index) => ({
          ...answer,
          correct: index === 0
        }))
      }

      return normalizedAnswers
    },

    setSingleCorrect(questionIndex, answerIndex) {
      this.surveyForm.questions[questionIndex].answers.forEach((answer, index) => {
        this.$set(answer, 'correct', index === answerIndex)
      })
    },

    isQuestionAnswersCountInvalid(question) {
      return question.answers.length < 2
    },

    isQuestionCorrectAnswerInvalid(question) {
      return !question.answers.some(answer => answer.correct)
    },

    isQuestionInvalid(question) {
      return this.isQuestionAnswersCountInvalid(question) || this.isQuestionCorrectAnswerInvalid(question)
    },

    validateSurveyQuestions() {
      const invalidAnswersQuestion = this.surveyForm.questions.find(question =>
        this.isQuestionAnswersCountInvalid(question)
      )
      if (invalidAnswersQuestion) {
        this.showNotification('error', 'Error', 'Add at least two answers to continue')
        this.focusQuestion(invalidAnswersQuestion.id)
        return false
      }

      const questionWithoutCorrectAnswer = this.surveyForm.questions.find(question =>
        this.isQuestionCorrectAnswerInvalid(question)
      )
      if (questionWithoutCorrectAnswer) {
        this.showNotification('error', 'Error', 'Add at least one correct answer to continue')
        this.focusQuestion(questionWithoutCorrectAnswer.id)
        return false
      }

      return true
    },

    saveQuestions() {
      this.$refs.surveyFormRef.validate(valid => {
        if (!valid) {
          this.showNotification('error', 'Error', 'Please fill out all required fields.')
          return
        }

        if (!this.validateSurveyQuestions()) {
          return
        }

        const updatedSurvey = {
          ...this.surveyForm,
          completion_rate:
            this.surveyForm.questions.length > 0
              ? (this.surveyForm.completion_rate / this.surveyForm.questions.length) * 100
              : 0,
          questions: this.surveyForm.questions.map(item => {
            return {
              ...item,
              id: typeof item.id === 'string' ? undefined : Number(item.id),
              type: item.type,
              answers: item.answers.map(answer => {
                return {
                  ...answer,
                  id: typeof answer.id === 'string' ? undefined : Number(answer.id),
                  question_id: typeof item.id === 'string' ? -1 : Number(item.id)
                }
              })
            }
          })
        }

        this.$emit('save', updatedSurvey)
      })
    }
  }
}
</script>

<style scoped>
.ant-form-item {
  margin-bottom: 0;
}

.survey-builder,
.survey-builder__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.survey-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
  border: 1px solid var(--border-color-base);
  padding: 16px;
  border-radius: 8px;
}

.survey-section_questions {
  padding-top: 18px;
}

.survey-section__header {
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 0;
}

.survey-section__header h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
}

.survey-section__header p {
  margin: 4px 0 0;
  color: var(--text-color-secondary);
  font-size: 13px;
  line-height: 1.45;
}

.survey-section__header_actions {
  align-items: flex-start;
}

.survey-settings {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.survey-score-preview {
  margin-top: 4px;
  color: var(--text-color-secondary);
  font-size: 12px;
  line-height: 1.4;
}

.survey-empty-state {
  border: 1px dashed var(--border-color-base);
  border-radius: 8px;
  padding: 24px;
}

.survey-empty-state__hint {
  max-width: 480px;
  margin: 0 auto 16px;
  color: var(--text-color-secondary);
  text-align: center;
}

.wrapper {
  max-height: 620px;
  overflow: auto;
  padding-right: 4px;
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.question-item {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 0 20px;
  border: 0;
  border-bottom: 1px solid var(--border-color-base);
  background-color: transparent;
}

.question-item:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.question-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.question-header p {
  margin: 4px 0 0;
  color: var(--text-color-secondary);
  font-size: 12px;
  line-height: 1.4;
}

.question-header h4 {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}

.question-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.question-item_invalid {
  padding-left: 12px;
  box-shadow: inset 3px 0 0 var(--error-color);
}

.question-validation-error {
  color: var(--error-color);
  font-size: 12px;
  line-height: 1.4;
}

.answers-wrapper {
  display: block;
}

.answers-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
}

.answers-block__header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  color: var(--text-color-secondary);
  font-size: 12px;
  line-height: 1.4;
}

.answers-block__header span:first-child {
  color: var(--text-color);
  font-size: 14px;
  font-weight: 600;
}

.answers-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.answer-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0;
  background-color: transparent;
  transition:
    background-color 0.2s,
    border-color 0.2s;
}

.answer-item:first-child {
  border-top: 0;
}

.answer-item:hover {
  background-color: rgba(255, 255, 255, 0.025);
}

.answer-input-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 40px 32px;
  gap: 8px;
  align-items: center;
  width: 100%;
}

.answer-input {
  min-width: 0;
}

.answer-correct-row {
  padding-left: 0;
  line-height: 1.4;
}

.question-drag-grip,
.answer-drag-grip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  color: var(--text-color-secondary);
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: grab;
  transition:
    background-color 0.2s,
    border-color 0.2s,
    color 0.2s;
}

.question-drag-grip {
  width: 32px;
  height: 32px;
}

.answer-drag-grip {
  width: 32px;
  height: 32px;
}

.question-header-actions:hover .question-drag-grip,
.answer-item:hover .answer-drag-grip {
  color: var(--text-color);
  background-color: rgba(255, 255, 255, 0.06);
  border-color: var(--border-color-base);
}

.drag-grip-dots {
  width: 14px;
  height: 18px;
  background-image: radial-gradient(currentColor 1.4px, transparent 1.4px);
  background-position: 0 0;
  background-size: 7px 7px;
}

.question-drag-grip:active,
.answer-drag-grip:active {
  cursor: grabbing;
}

.ghost {
  opacity: 0.5;
  background-color: rgba(59, 130, 246, 0.08);
  border-style: dashed;
}

.drag-chosen {
  border-color: #3b82f6;
}

.drag-active {
  cursor: grabbing;
}

.flip-list-move {
  transition: transform 0.5s;
}
</style>
